<script setup>
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { computed, reactive, ref, watch } from 'vue';
import { object, string } from 'yup';

const {
    tenant,
    eCommerceProducts,
    aiProductContent,
    customFields,
    productAttributes,
    defaultContextAttributeCodes,
    callback,
} = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    eCommerceProducts: {
        type: [Array, Function],
        required: true,
    },
    aiProductContent: {
        type: Object,
        required: false,
    },
    customFields: {
        type: Array,
        required: false,
        default: () => [],
    },
    productAttributes: {
        type: Array,
        required: false,
        default: () => [],
    },
    defaultContextAttributeCodes: {
        type: Array,
        required: false,
        default: () => [],
    },
    callback: {
        type: Function,
        required: true,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const isOpen = ref(false);

const innerECommerceProducts = ref(
    typeof eCommerceProducts === 'function' ? [] : eCommerceProducts,
);

const baseFieldOptions = [
    { key: 'description', label: 'Description' },
    { key: 'short_description', label: 'Description courte' },
    { key: 'meta_title', label: 'Méta-titre' },
    { key: 'meta_description', label: 'Méta-description' },
];

const fieldOptions = computed(() => [
    ...baseFieldOptions,
    ...customFields.map((field) => ({
        key: field.cmsAttributeCode,
        label: field.label,
    })),
]);

const initialSelection = aiProductContent?.generationFields?.length
    ? aiProductContent.generationFields
    : ['description', 'short_description'];

const selectedFields = reactive(
    Object.fromEntries(
        fieldOptions.value.map((option) => [
            option.key,
            initialSelection.includes(option.key),
        ]),
    ),
);

const attributeOptions = computed(() =>
    productAttributes.map((attribute) => ({
        ...attribute,
        label: attribute.label || attribute.code,
    })),
);

const initialContextCodes = aiProductContent?.contextAttributeCodes
    ? aiProductContent.contextAttributeCodes
    : defaultContextAttributeCodes;

const form = reactive({
    additionalInstructions: aiProductContent
        ? aiProductContent.additionalInstructions
        : '',
    contextAttributes: attributeOptions.value.filter((attribute) =>
        initialContextCodes.includes(attribute.code),
    ),
});

const selectedKeys = computed(() =>
    fieldOptions.value
        .map((option) => option.key)
        .filter((key) => selectedFields[key]),
);

const validationSchema = object({
    additionalInstructions: string().nullable(),
});

function updateData() {
    if (typeof eCommerceProducts === 'function') {
        Promise.resolve(eCommerceProducts()).then((result) => {
            innerECommerceProducts.value = result;
        });
    }
}

function submit() {
    if (selectedKeys.value.length === 0) {
        toastError('Sélectionne au moins un champ à générer.');

        return;
    }

    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.ai.postScheduleProductContentGeneration',
                {
                    tenant: tenant.id,
                },
            ),
            {
                cmsProductIds: innerECommerceProducts.value.map(
                    (product) => product.cmsProductId,
                ),
                generationFields: selectedKeys.value,
                additionalInstructions: form.additionalInstructions,
                contextAttributeCodes: form.contextAttributes.map(
                    (attribute) => attribute.code,
                ),
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}

watch(
    () => isOpen.value,
    (newValue) => {
        if (newValue) {
            updateData();
        }
    },
);
</script>

<template>
    <DialogModal v-model="isOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title>
            Programmer la génération de contenu pour
            {{ innerECommerceProducts.length }} produit(s)
        </template>

        <template #full-content>
            <FormRoot
                v-slot="{ errors }"
                :validation-schema="validationSchema"
                :submit="submit"
            >
                <FormColumn>
                    <FormContent>
                        <div class="mb-4 flex flex-col gap-2">
                            <span class="font-medium">
                                Champs à générer
                                <sup class="text-red-500">*</sup>
                            </span>

                            <SwitchField
                                v-for="option in fieldOptions"
                                :key="option.key"
                                v-model="selectedFields[option.key]"
                                :field-id="`generationField-${option.key}`"
                                :field-name="`generationField-${option.key}`"
                                :label="option.label"
                                inline
                            />
                        </div>

                        <ComboboxField
                            v-if="attributeOptions.length > 0"
                            v-model="form.contextAttributes"
                            field-name="contextAttributes"
                            field-id="contextAttributes"
                            filtered-option-key="label"
                            :options="attributeOptions"
                            by="code"
                            multiple
                            label="Attributs produits fournis à l'IA"
                            legend="Leurs valeurs stockées sont transmises comme données factuelles à l'IA."
                        >
                            <template #optionContent="{ option }">
                                {{ option.label }}
                                <span class="text-xs text-gray-400">
                                    ({{ option.code }})
                                </span>
                            </template>
                        </ComboboxField>

                        <TextareaField
                            v-model="form.additionalInstructions"
                            field-name="additionalInstructions"
                            field-id="additionalInstructions"
                            label="Instructions supplémentaires"
                            rows="8"
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                    submit-text="Programmer"
                    submitting-text="Programmation en cours..."
                />
            </FormRoot>
        </template>
    </DialogModal>
</template>
